import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Card, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import axios from 'axios';
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomTextarea from "../../common/CustomTextarea";
import { UPDATE_COO_EXPORT, READ_COO_EXPORT } from '../../../services/apiRoutes';
import CustomStyle from "../../common/CustomStyle";
import { t } from "../../../constants/translationHelper";

const ValidationSchema = Yup.object().shape({
    exporter: Yup.string().required('Exporter Name is required'),
    serialNo: Yup.string().required('Serial Number is required'),
    certificateNo: Yup.string().required('Certificate Number is required'),
    consignee: Yup.string().required('Consignee Name is required'),
    certifyingAuthority: Yup.string().required('Certifying Authority is required'),
    marksAndPackages: Yup.string().required('Marks and Packages Description is required'),
    descriptionOfGoods: Yup.string().required('Description of Goods is required'),
    hsCode: Yup.string().required('HS Code is required'),
    quantity: Yup.string().required('Quantity is required'),
    invoiceDetails: Yup.string().required('Invoice Details are required'),
});

const UpdateCOO = ({ handleClose, setMessage, fetchData, cooId, mailingCountry, exporter, consignee }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!cooId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_COO_EXPORT(cooId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                setData(response.data.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [cooId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            const response = await axios.put(UPDATE_COO_EXPORT(cooId), values, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "updated successfully",
                    type: "success",
                });
                fetchData();
                setTimeout(() => {
                    handleClose();
                },);
            } else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={{
                    exporter: data?.exporter || '',
                    serialNo: data?.serialNo || '',
                    certificateNo: data?.certificateNo || '',
                    consignee: data?.consignee || '',
                    transportAndRoute: data?.transportAndRoute || '',
                    certifyingAuthority: data?.certifyingAuthority || '',
                    countryOrRegion: data?.countryOrRegion || '',
                    marksAndPackages: data?.marksAndPackages || '',
                    descriptionOfGoods: data?.descriptionOfGoods || '',
                    hsCode: data?.hsCode || '',
                    quantity: data?.quantity || '',
                    invoiceDetails: data?.invoiceDetails || '',
                    declarationByExporter: data?.declarationByExporter || '',
                    certification: data?.certification || '',

                }}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Update COO")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>


                        <Divider />
                        <Grid container spacing={1} style={{ paddingBottom: "2px", paddingTop: "2px" }}>
                            <Grid item xs={4} md={12}>
                                <Typography style={{
                                    color: "#212529",
                                    fontWeight: 500, fontSize: "20px",
                                    margin: "auto",
                                    padding: "auto",
                                    textAlign: "center",
                                    textTransform: "uppercase"
                                }}>
                                    {t("Original")}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container spacing={1} >
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '30px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Exporter")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            readOnly={true}
                                            required={true}
                                            name="exporter"
                                            value={values.exporter}
                                            onChange={handleChange}
                                            placeholder="Enter exporter"
                                            error={touched.exporter && !!errors.exporter}
                                            helperText={touched.exporter && errors.exporter}
                                            sx={{ height: '65px !important', textTransform: 'capitalize', ...CustomStyle.readOnly }}
                                        />
                                    </Typography>

                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '30px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Consignee")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            readOnly={true}
                                            required={true}
                                            name="consignee"
                                            value={values.consignee}
                                            onChange={handleChange}
                                            placeholder="Enter consignee"
                                            error={touched.consignee && !!errors.consignee}
                                            helperText={touched.consignee && errors.consignee}
                                            sx={{ height: '65px !important', textTransform: 'capitalize', ...CustomStyle.readOnly }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <Card variant="outlined" style={{ backgroundColor: "#e7f0f7" }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '5px' }}>
                                        <Box sx={{ width: '130px' }}>
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px", marginRight: '16px' }}>
                                                {t("Serial No.")}
                                            </Typography>
                                        </Box>
                                        <CustomTextField
                                            name="serialNo"
                                            value={values.serialNo}
                                            required={true}
                                            onChange={handleChange}
                                            placeholder="Enter serialNo No"
                                            error={touched.serialNo && !!errors.serialNo}
                                            helperText={touched.serialNo && errors.serialNo}
                                            sx={{ backgroundColor: "#fff" }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '5px' }}>
                                        <Box sx={{ width: '130px' }}>
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px", marginRight: '16px' }}>
                                                {t("Certificate No.")}
                                            </Typography>
                                        </Box>
                                        <CustomTextField
                                            name="certificateNo"
                                            value={values.certificateNo}
                                            required={true}
                                            onChange={handleChange}
                                            placeholder="Enter certificate No"
                                            error={touched.certificateNo && !!errors.certificateNo}
                                            helperText={touched.certificateNo && errors.certificateNo}
                                            sx={{ backgroundColor: "#fff" }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                                        <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px", textAlign: 'center' }}>
                                            {t("Certification of Origin")}
                                            <br />
                                            {t("of")}
                                            <br />
                                            {t("the People's Republic Of")} {mailingCountry}
                                            <br />
                                            <br />
                                            <br />
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid>


                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '30px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Means Of Transport And Route")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="transportAndRoute"
                                            value={values.transportAndRoute}
                                            onChange={handleChange}
                                            placeholder="Enter Means Of Transport And Route"
                                            sx={{ height: '65px !important' }}
                                        />
                                    </Typography>

                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '30px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Country / Region of Destination")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="countryOrRegion"
                                            value={values.countryOrRegion}
                                            onChange={handleChange}
                                            placeholder="Enter Country / Region of Destination"
                                            sx={{ height: '65px !important' }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>


                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '30px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("For Certifiying authority use Only")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="certifyingAuthority"
                                            value={values.certifyingAuthority}
                                            required={true}
                                            onChange={handleChange}
                                            error={touched.certifyingAuthority && !!errors.certifyingAuthority}
                                            helperText={touched.certifyingAuthority && errors.certifyingAuthority}
                                            placeholder="Enter For Certifiying authority use Only"
                                            sx={{ height: '180px !important' }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={3} md={2} lg={2}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '50px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Marks and No.")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="marksAndPackages"
                                            value={values.marksAndPackages}
                                            required={true}
                                            onChange={handleChange} marksAndPackages
                                            error={touched.marksAndPackages && !!errors.marksAndPackages}
                                            helperText={touched.marksAndPackages && errors.marksAndPackages}
                                            placeholder="Enter Marks and No."
                                            sx={{ height: '250px !important' }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={3}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '50px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("No. and kind of packages; Description of Goods")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="descriptionOfGoods"
                                            value={values.descriptionOfGoods}
                                            required={true}
                                            onChange={handleChange}
                                            error={touched.descriptionOfGoods && !!errors.descriptionOfGoods}
                                            helperText={touched.descriptionOfGoods && errors.descriptionOfGoods}
                                            placeholder="Enter No. and kind of packages; Description of Goods"
                                            sx={{ height: '250px !important' }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={3} md={2} lg={2}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '50px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("H.S Code")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="hsCode"
                                            value={values.hsCode}
                                            required={true}
                                            onChange={handleChange}
                                            error={touched.hsCode && !!errors.hsCode}
                                            helperText={touched.hsCode && errors.hsCode}
                                            placeholder="Enter H.S Code"
                                            sx={{ height: '250px !important' }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={3} md={2} lg={2}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '50px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Quantity")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="quantity"
                                            value={values.quantity}
                                            required={true}
                                            onChange={handleChange}
                                            error={touched.quantity && !!errors.quantity}
                                            helperText={touched.quantity && errors.quantity}
                                            placeholder="Enter Quantity"
                                            sx={{ height: '250px !important' }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={3}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '50px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("No. and date of Invoice")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="invoiceDetails"
                                            value={values.invoiceDetails}
                                            required={true}
                                            onChange={handleChange}
                                            error={touched.invoiceDetails && !!errors.invoiceDetails}
                                            helperText={touched.invoiceDetails && errors.invoiceDetails}
                                            placeholder="Enter No. and date of Invoice"
                                            sx={{ height: '250px !important' }}
                                        />
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '110px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Declaration by Exporter")}
                                            </Typography>
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "13px" }}>
                                                {t("The undersigned hereby declares that the above details and statements are correct, that the goods were produced in ")} ({mailingCountry} ){t("and that they comply with the rules of origin of the People's Republic of ")} ({mailingCountry}).
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="declarationByExporter"
                                            value={values.declarationByExporter}
                                            onChange={handleChange}
                                            placeholder="Enter Declaration by Exporter"
                                            sx={{ height: '80px !important' }}
                                        />
                                    </Typography>
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '50px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Place and Date, Signature and stamp of Authorized Signatory")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <Card variant='outlined' >
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '110px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Certification")}
                                            </Typography>
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "13px" }}>
                                                {t("It is hereby certified that the declaration by the exporter is correct")}.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Typography style={{ padding: "2px" }}>
                                        <CustomTextarea
                                            name="certification"
                                            value={values.certification}
                                            onChange={handleChange}
                                            placeholder="Enter Certification"
                                            sx={{ height: '80px !important' }}
                                        />
                                    </Typography>
                                    <Box display="flex" flexDirection="row" paddingLeft={1} style={{ height: '50px', backgroundColor: "#e7f0f7" }}>
                                        <Box >
                                            <Typography style={{ fontWeight: 500, color: "#212529", fontSize: "16px" }}>
                                                {t("Place and Date, Signature and stamp of Certifying Authority")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Card>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdateCOO;


